import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import {
	FormControlLabel,
	Grid,
	GridProps,
	Switch,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

// Utility
import Utility from "../../domain/classes/Utility";

// Components
import StyledConcessionCard from "./ConcessionCard.Component";

// Font
import "@fontsource/share-tech-mono";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import { InitialConcessionCardErrorState } from "../../domain/interfaces/IConcessionCardError";
import { InitialConcessionCardState } from "../../domain/interfaces/IConcessionCard";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
		errorText: {},
	});

export interface ConcessionCardContainerProps extends GridProps {}

const ConcessionCardContainer = React.forwardRef<
	HTMLDivElement,
	ConcessionCardContainerProps & WithStyles<typeof styles>
>(({ classes, className, ...gridProps }, ref) => {
	// Contexts
	// const { patientDetails, handlePatientDetailsChange } = usePatientManager();
	const {
		patientDetails,
		handlePatientDetailsChange,
		concessionCardError,
		handleConcessionCardChange,
		handleConcessionCardErrorChange,
	} = useApplicationManager();

	// Handlers
	const handleHasConcessionToggle = () => {
		if (patientDetails.hasConcession) {
			handleConcessionCardChange({ ...InitialConcessionCardState });
			handleConcessionCardErrorChange({
				...InitialConcessionCardErrorState,
			});
		}

		handlePatientDetailsChange(
			"hasConcession",
			!patientDetails.hasConcession
		);
	};

	return (
		<React.Fragment>
			{patientDetails.hasMedicare && (
				<Grid
					container
					spacing={1}
					ref={ref}
					{...gridProps}
					className={clsx(classes.root, className)}
				>
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={patientDetails.hasConcession}
									onChange={handleHasConcessionToggle}
									name="Healthcare card"
									color="primary"
									disabled={!patientDetails.hasMedicare}
								/>
							}
							label="Do you have a Healthcare Card?"
						/>
					</Grid>
					{patientDetails.hasConcession && (
						<React.Fragment>
							<Grid item xs={12}>
								<StyledConcessionCard />
							</Grid>
							{JSON.stringify(concessionCardError) !==
								JSON.stringify(
									InitialConcessionCardErrorState
								) && (
								<Grid item xs={12}>
									<Alert severity="error" component="div">
										{concessionCardError.cardNumberError && (
											<Typography
												className={classes.errorText}
												variant="caption"
												display="block"
											>
												{
													concessionCardError.cardNumberError
												}
											</Typography>
										)}
										{concessionCardError.letterError && (
											<Typography
												className={classes.errorText}
												variant="caption"
												display="block"
											>
												{
													concessionCardError.letterError
												}
											</Typography>
										)}
										{concessionCardError.expireAtError && (
											<Typography
												className={classes.errorText}
												variant="caption"
												display="block"
											>
												{
													concessionCardError.expireAtError
												}
											</Typography>
										)}
									</Alert>
								</Grid>
							)}
						</React.Fragment>
					)}
				</Grid>
			)}
		</React.Fragment>
	);
});

const StyledConcessionCardContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("ConcessionCardContainer"),
})(ConcessionCardContainer);

export default StyledConcessionCardContainer;

export const AnimatedConcessionCardContainer = animated(
	StyledConcessionCardContainer
);
